import type { InstagramDestinationPlatformData } from './destination-details.interfaces';
import {
  INSTAGRAM_DEFAULT_POST_TYPE,
  INSTAGRAM_POST_TYPES,
} from './instagram-post-types';

/** Max Instagram Graph caption length (title + message + hashtags). */
export const INSTAGRAM_CAPTION_MAX_LENGTH = 2200;

/** Per-publication Instagram override stored in publish_metadata_overrides_json.destinationSettings. */
export interface InstagramPublicationSettings {
  default_post_type: string | null;
  share_to_feed: boolean | null;
  is_ai_generated: boolean | null;
}

const ALLOWED_POST_TYPES = new Set(
  INSTAGRAM_POST_TYPES.map((option) => option.value),
);

export function mapInstagramDefaultsToPublicationSettings(
  defaults: InstagramDestinationPlatformData | null | undefined,
): InstagramPublicationSettings {
  const rawPostType = defaults?.default_post_type?.trim();
  const postType =
    rawPostType && ALLOWED_POST_TYPES.has(rawPostType)
      ? rawPostType
      : INSTAGRAM_DEFAULT_POST_TYPE;
  return {
    default_post_type: postType,
    share_to_feed: defaults?.share_to_feed ?? true,
    is_ai_generated: defaults?.is_ai_generated ?? false,
  };
}

export function normalizeInstagramPublicationSettings(
  settings: InstagramPublicationSettings,
): InstagramPublicationSettings {
  const rawPostType = settings.default_post_type?.trim();
  const postType =
    rawPostType && ALLOWED_POST_TYPES.has(rawPostType)
      ? rawPostType
      : INSTAGRAM_DEFAULT_POST_TYPE;
  return {
    default_post_type: postType,
    share_to_feed: settings.share_to_feed ?? true,
    is_ai_generated: settings.is_ai_generated ?? false,
  };
}

/** Merges saved override with destination defaults. */
export function resolveInstagramPublicationSettings(
  saved: unknown,
  defaults: InstagramDestinationPlatformData | null | undefined,
): InstagramPublicationSettings {
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    const obj = saved as Record<string, unknown>;
    if (
      'default_post_type' in obj ||
      'share_to_feed' in obj ||
      'is_ai_generated' in obj
    ) {
      return normalizeInstagramPublicationSettings({
        default_post_type: (obj.default_post_type as string | null) ?? null,
        share_to_feed: obj.share_to_feed as boolean | null | undefined,
        is_ai_generated: obj.is_ai_generated as boolean | null | undefined,
      });
    }
  }
  return mapInstagramDefaultsToPublicationSettings(defaults);
}

/**
 * Approximate Instagram caption assembly (matches backend InstagramPublishMetadataBuilder.BuildCaption).
 */
export function buildInstagramCaptionPreview(
  title: string | null | undefined,
  message: string | null | undefined,
  tags: string | null | undefined,
): string {
  const parts: string[] = [];
  addIfNotEmpty(parts, title);
  addIfNotEmpty(parts, message);
  const hashtags = buildHashtags(tags);
  addIfNotEmpty(parts, hashtags);
  return parts.join('\n');
}

function addIfNotEmpty(parts: string[], value: string | null | undefined): void {
  const trimmed = value?.trim();
  if (trimmed) {
    parts.push(trimmed);
  }
}

function buildHashtags(tags: string | null | undefined): string | null {
  if (!tags?.trim()) {
    return null;
  }
  const tagParts = tags
    .trim()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .map((t) => (t.startsWith('#') ? t : `#${t}`));
  return tagParts.length === 0 ? null : tagParts.join(' ');
}
